'use client'

import { useState, useMemo } from 'react'
import { ProjectCard } from '@excellent/ui/blocks/lens/projects/project-card'
import { Skeleton } from "@excellent/ui"
import { Alert, AlertDescription } from "@excellent/ui"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@excellent/ui"
import { PageHeader, SearchInput } from "@excellent/ui"
import { AlertTriangle, ArrowUpDown } from "@excellent/ui/icons"
import { useSnapshot } from '@lens/lib/use-snapshot'

type SortKey = 'last_active' | 'estimated_cost' | 'session_count' | 'total_duration_minutes'

const SORT_OPTIONS: { k: SortKey; label: string }[] = [
 { k: 'last_active', label: 'Recent' },
 { k: 'estimated_cost', label: 'Cost' },
 { k: 'session_count', label: 'Sessions' },
 { k: 'total_duration_minutes', label: 'Time' },
]

export function ProjectsClient() {
 const { snapshot, error, isLoading } = useSnapshot()
 const [sort, setSort] = useState<SortKey>('last_active')
 const [search, setSearch] = useState('')

 const sorted = useMemo(() => {
 if (!snapshot) return []
 let projects = [...snapshot.projects]
 if (search) {
 const q = search.toLowerCase()
 projects = projects.filter(p =>
 p.display_name.toLowerCase().includes(q) ||
 p.project_path.toLowerCase().includes(q)
 )
 }
 return projects.sort((a, b) => {
 if (sort === 'last_active') return b.last_active.localeCompare(a.last_active)
 return (b[sort] as number) - (a[sort] as number)
 })
 }, [snapshot, sort, search])

 return (
 <div className="flex flex-col min-h-screen">
 <div className="px-6 py-5 space-y-4">

 <PageHeader
 title="Projects"
 description="Every Claude Code project on this machine — cost, sessions, and time."
 toolbar={
 <>
 <SearchInput
 value={search}
 onChange={e => setSearch(e.target.value)}
 placeholder="Search projects…"
 width="sm"
 />
 <div className="flex items-center gap-2">
 <ArrowUpDown className="size-4 text-muted-foreground" />
 <Select value={sort} onValueChange={v => setSort(v as SortKey)}>
 <SelectTrigger className="w-36">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {SORT_OPTIONS.map(({ k, label }) => (
 <SelectItem key={k} value={k}>{label}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </>
 }
 />

 {error && (
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription>Error loading data: {String(error)}</AlertDescription>
 </Alert>
 )}

 {isLoading && (
 <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
 {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-48 rounded-lg" />)}
 </div>
 )}

 {sorted.length > 0 && (
 <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
 {sorted.map(p => <ProjectCard key={p.slug} project={p} />)}
 </div>
 )}

 {!isLoading && sorted.length === 0 && (
 <div className="text-center py-16 text-foreground type-body">
 {search ? 'No projects match your search.' : 'No projects found in ~/.claude/'}
 </div>
 )}
 </div>
 </div>
 )
}
